<script lang="ts">
  import { fly, fade } from 'svelte/transition';
  import { expoInOut } from 'svelte/easing';
  import { onMount } from 'svelte';
  import { SessionModel } from '@24hbs/utils';
  import { TABLET_BREAKPOINT } from '$lib/utils/responsive';
  import { dateShortMonthFormat } from '$lib/utils/formatter';
	import { LinkAction } from '$lib/components/atoms/Link';

  export let session: SessionModel = new SessionModel();
  export let stickyTarget: string | undefined;
  export let showCartContent: boolean = false;
  export let customHideStickyHeader: boolean = false;

  let courseCartEl: HTMLElement;
  let stickyThreshold = 200; // default
  let showStickyHeader = false;
  let showStickyFooter = false;
  let stickyHeaderEl: HTMLElement;
  let stickyFooterEl: HTMLElement;
  let cartContentEl: HTMLElement;
  let slots = $$props && $$props.slots ? $$props.slots : {};
  let promoLabel: HTMLElement;

  $: {
    if (typeof window !== 'undefined') {
      if (showCartContent) {
        document?.body.classList.add('is-navigation-open');
      } else {
        document?.body.classList.remove('is-navigation-open');
      }
    }
  }

  $: {
    if (typeof window !== 'undefined' && stickyFooterEl) {
      if (showStickyFooter) {
        document.body.style.paddingBottom = `${stickyFooterEl.clientHeight}px`;
      } else {
        document.body.style.paddingBottom = '';
      }
    }
  }

  onMount(() => {
    updateStickyElements();

    const target = document.querySelector(stickyTarget);
    if (target) {
      const bodyTop = document.body.getBoundingClientRect().top;
      stickyThreshold = target.getBoundingClientRect().top - bodyTop;
    }
  });

  const updateStickyElements = () => {
    const promoLabel: HTMLElement = document.querySelector('.timer-label');

    if (document.documentElement.clientWidth < TABLET_BREAKPOINT) {
      const shouldShow = document.documentElement.scrollTop >= stickyThreshold;

      // sticky header
      if (!showStickyHeader && shouldShow) {
        if (promoLabel) promoLabel.style.setProperty('visibility', 'hidden');
        showStickyHeader = true;
      }
      if (showStickyHeader && !shouldShow) {
        if (promoLabel) promoLabel.style.setProperty('visibility', 'visible');
        showStickyHeader = false;
      }

      // sticky footer
      if (showCartContent) {
        showStickyFooter = false;
      }
      if (!showStickyFooter && shouldShow && !showCartContent) {
        showStickyFooter = true;
      }
      if (showStickyFooter && !shouldShow) {
        showStickyFooter = false;
      }
    }
  };

  const toggleShowCartContent = () => {
    showCartContent = !showCartContent;
    updateStickyElements();
  };
</script>

<svelte:window on:scroll={updateStickyElements} />

<div bind:this={courseCartEl} class="side-course-cart">
  {#if showStickyHeader && !customHideStickyHeader}
    <div
      class="sticky-header"
      in:fly={{
        opacity: 1,
        y: -stickyHeaderEl.clientHeight,
        easing: expoInOut,
      }}
      out:fly={{
        opacity: 1,
        y: -stickyHeaderEl.clientHeight,
        easing: expoInOut,
      }}
      bind:this={stickyHeaderEl}
    >
      <div class="sticky-header-content">
        <div class="sticky-header-title">Info e costi</div>
        <div class="sticky-header-date">
          {#if session.startDate}
            <span class="date">{dateShortMonthFormat(session.startDate).toUpperCase()}</span>
          {/if}
          {#if session.endDate}
            <span class="date">{dateShortMonthFormat(session.endDate).toUpperCase()}</span>
          {/if}
        </div>
      </div>
      <div class="sticky-header-toggler">
        <LinkAction icon={showCartContent ? 'minus' : 'plus'} on:click={toggleShowCartContent} />
      </div>
    </div>
  {/if}

  <div bind:this={cartContentEl}>
    <div class="header space-y-4">
      <slot name="header" />
    </div>
    <div class="company-flag px-4 pt-4 pb-0">
      <slot name="company-flag" />
    </div>
    <div class="course-date-price space-y-6 pb-0">
      <slot name="course-date-price" />
    </div>
    <div class="course-info">
      <slot name="course-info" />
    </div>
  </div>
  {#if slots.footer}
    <div class="footer">
      <slot name="footer" />
    </div>
  {/if}
  {#if showCartContent && cartContentEl}
    <div class="cart-content-modal" in:fade={{ duration: 200 }} out:fade={{ duration: 200 }}>
      <div>
        <div class="header space-y-4">
          <slot name="header" />
        </div>
        <div class="course-date-price space-y-6">
          <slot name="course-date-price" />
        </div>
        <div class="course-info">
          <slot name="course-info" />
        </div>
      </div>
    </div>
  {/if}

  {#if $$slots['sticky-footer'] && showStickyFooter}
    <div
      class="sticky-footer"
      in:fly={{ opacity: 1, y: stickyFooterEl.clientHeight, easing: expoInOut }}
      out:fly={{
        opacity: 1,
        y: stickyFooterEl.clientHeight,
        easing: expoInOut,
      }}
      bind:this={stickyFooterEl}
    >
      <slot name="sticky-footer" />
    </div>
  {/if}
</div>

<style type="text/scss" lang="scss" scoped>
  .side-course-cart {
    @apply flex flex-col justify-between;
    @apply h-full;
  }

  .header,
  .course-date-price,
  .course-info,
  .footer {
    @apply page-margin py-5 md:p-5;
  }
  .header {
    @apply bg-gray-900 grid;
  }

  .course-date-price {
    @apply border-b border-gray-100;
  }

  .footer {
    @apply grid;
    @apply pt-0;
  }

  .sticky-header {
    @apply fixed top-0 inset-x-0 z-over-sticky-element;
    @apply px-4 py-3;
    @apply bg-white shadow-card;
    @apply flex items-center;
    @apply md:hidden;
  }

  .sticky-header-content {
    @apply flex flex-col;
  }
  .sticky-header-title {
    @apply font-serif font-medium text-md;
    @apply leading-normal;
  }
  .sticky-header-date {
    @apply text-xs uppercase;
    @apply leading-none;
    @apply tracking-widest;
  }
  .date + .date {
    &::before {
      content: '';
      @apply inline-block align-middle;
      @apply w-7 h-px;
      @apply mx-1.5;
      @apply bg-petrol;
    }
  }
  .sticky-header-toggler {
    @apply ml-auto;
  }

  .sticky-footer {
    @apply fixed bottom-0 inset-x-0 z-over-sticky-element;
    @apply flex flex-col;
    @apply p-4;
    @apply bg-gray-900 shadow-card;
    @apply md:hidden;
  }
  :global(.side-course-cart .sticky-footer > * + *) {
    @apply mt-4;
  }

  .cart-content-modal {
    @apply fixed inset-0;
    @apply bg-white;
    @apply pt-16 pb-4;
    @apply overflow-y-auto;
  }
</style>
